import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { useParams, Link } from "react-router-dom";
import {
  TbScanPosition,
  TbTruckDelivery,
  TbClipboardCheck,
  TbTruck,
  TbDownload,
  TbLoader,
  TbPhoto,
  TbFileSearch,
  TbChevronDown, // Ícone para indicar "expandir"
  TbChevronUp,   // Ícone para indicar "recolher"
} from "react-icons/tb";
import GroupedContainer from "../../Components/GroupedContainer";
import InfoSection from "../../Components/InfoSection";
import { VehicleImagePopup } from "../../Components/VehicleImagePopup";

// Componente de UI reutilizável
function BentoBox({ title, icon: Icon, children, className }) {
  return (
    <div className={`p-4 bg-[#292826] rounded-lg shadow ${className}`}>
      <h3 className="text-lg font-semibold mb-2 flex items-center">
        <Icon className="mr-2" />
        {title}
      </h3>
      {children}
    </div>
  );
}

// Componente para gerenciar o download de ZIPs para um único veículo
function VehicleZipDownloader({ loadId, vehicleId }) {
    const [zipLinks, setZipLinks] = useState([]);
    const [isLoadingZips, setIsLoadingZips] = useState(false);
    const [zipError, setZipError] = useState(null);

    const handleGenerateZips = async () => {
        setIsLoadingZips(true);
        setZipError(null);
        setZipLinks([]);
        try {
            const response = await axios.get(`https://new.cajuu.app/api/loadtransport/${loadId}/generate-photo-zips`);
            const vehicleSpecificLinks = response.data.files.filter(file => file.includes(`_${vehicleId}_`));
            if (vehicleSpecificLinks.length > 0) {
                setZipLinks(vehicleSpecificLinks);
            } else {
                setZipError("No photo files were found to generate archives for this vehicle.");
            }
        } catch (err) {
            const errorMessage = err.response?.data?.message || "An error occurred.";
            setZipError(errorMessage);
        } finally {
            setIsLoadingZips(false);
        }
    };
    
    return (
        <div className="mt-3">
            <button onClick={handleGenerateZips} disabled={isLoadingZips} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg flex items-center justify-center transition-all duration-200 disabled:bg-gray-600 disabled:cursor-not-allowed">
                {isLoadingZips ? <><TbLoader className="animate-spin mr-2" />Generating...</> : <><TbDownload className="mr-2"/>Generate ZIP Archives</>}
            </button>
            {zipError && <p className="text-red-500 text-sm mt-2">{zipError}</p>}
            {zipLinks.length > 0 && (
                <div className="mt-3 space-y-1"><h4 className="font-semibold text-sm mb-1">Download Links:</h4>
                    {zipLinks.map((link, index) => <a key={index} href={`https://new.cajuu.app${link}`} download target="_blank" rel="noopener noreferrer" className="block text-blue-400 hover:underline text-xs truncate">{link.split('/').pop()}</a>)}
                </div>
            )}
        </div>
    );
}

// Componente para exibir um item da lista de inspeção
function InspectionDataItem({ label, value }) {
    // Função para formatar o valor. Se não for 'Yes' ou 'No', exibe o valor original ou N/A.
    const formattedValue = () => {
        if (value === 'Yes' || value === 'No') {
            return value;
        }
        return value || 'N/A';
    };

    return (
        <li className="flex justify-between border-b border-gray-700 py-1 text-xs">
            <span className="text-gray-400">{label}:</span>
            <span className="font-semibold">{formattedValue()}</span>
        </li>
    );
}

// Componente para gerenciar a exibição de um único card de veículo
function VehicleCard({ vehicle, orderId, loadId, onVehicleClick }) {
    // 1. ADICIONAR ESTADO PARA CONTROLAR O CARD
    // Começa expandido por padrão (true)
    const [isExpanded, setIsExpanded] = useState(true);

    const getImageUrl = (path) => {
        if (!path) return "/img/cajuu_logo.png";
        return `https://new.cajuu.app/storage/${path}`;
    };

    // 2. FUNÇÃO PARA ALTERNAR O ESTADO
    const handleToggleExpand = () => {
      setIsExpanded(prevState => !prevState);
    };
    
    // Função para abrir o popup da imagem sem disparar o toggle
    const handleImageClick = (e) => {
      e.stopPropagation(); // Impede que o clique "borbulhe" para o div pai
      onVehicleClick(vehicle);
    };

    const latestInspection = useMemo(() => {
        if (!vehicle.inspection_load_items || vehicle.inspection_load_items.length === 0) return null;
        const dropoff = vehicle.inspection_load_items.find(insp => insp.inspection_type === 'Dropoff');
        return dropoff || vehicle.inspection_load_items.find(insp => insp.inspection_type === 'Pickup') || null;
    }, [vehicle.inspection_load_items]);
    
    const hasPickupPhotos = useMemo(() => vehicle.inspection_photos?.some(p => p.inspection_type === 'Pickup'), [vehicle.inspection_photos]);
    const hasDropoffPhotos = useMemo(() => vehicle.inspection_photos?.some(p => p.inspection_type === 'Dropoff'), [vehicle.inspection_photos]);

    // Função para converter 1/0 para Yes/No
    const toYesNo = (value) => {
        if (value === 1) return 'Yes';
        if (value === 0) return 'No';
        return 'N/A'; // Retorna N/A se o valor não for nem 1 nem 0
    };

    return (
        <div key={vehicle.id} className="mb-3 bg-[#1e1d1b] p-3 rounded-lg flex flex-col gap-3">
            {/* Informações Básicas do Veículo */}
            <div className="flex items-center justify-between cursor-pointer" onClick={handleToggleExpand}>
                <div className="flex items-center gap-3">
                  <img
                      src={getImageUrl(vehicle.profile_picture_path)}
                      alt={`${vehicle.make} ${vehicle.model}`}
                      width={50} height={50}
                      className="mr-3 rounded object-cover flex-shrink-0"
                      onClick={handleImageClick} 
                  />
                  <div>
                      <p className="text-sm font-semibold">{vehicle.year} {vehicle.make} {vehicle.model}</p>
                      <p className="text-xs text-gray-400">VIN: {vehicle.vin || "N/A"}</p>
                  </div>
                </div>
                {isExpanded ? <TbChevronUp size={20} /> : <TbChevronDown size={20} />}
            </div>

            {/* 5. ENVOLVER O CONTEÚDO COLAPSÁVEL EM UMA CONDIÇÃO */}
            {isExpanded && (
                <>
                  {/* Seção de Inspeção com Novo Layout */}
                  <div className="border-t border-gray-700 pt-3">
                      {latestInspection ? (
                          <div>
                              <h4 className="text-sm font-bold flex items-center mb-2"><TbFileSearch className="mr-2"/>Inspection Details - {latestInspection.inspection_type}</h4>
                              <div className="flex flex-col md:flex-row gap-4 mt-2">
                                  {/* Coluna Esquerda: Diagrama */}
                                  <div className="md:w-1/2">
                                      <img src={getImageUrl(latestInspection.diagram_path)} alt={`Diagram for ${latestInspection.inspection_type}`} className="w-full rounded-lg border-2 border-gray-600"/>
                                  </div>
                                  {/* Coluna Direita: Dados da Inspeção */}
                                  <div className="md:w-1/2 bg-[#292826] p-3 rounded-lg">
                                      <ul className="space-y-1">
                                          <li className="text-sm font-bold pb-1 mb-1 border-b border-gray-700">
                                              {vehicle.year} {vehicle.make} {vehicle.model} ({vehicle.is_operable === 1 ? 'OP' : 'INOP'})
                                          </li>
                                          <InspectionDataItem label="Odometer" value={latestInspection.odometer} />
                                          {/* ======================= CORREÇÃO APLICADA AQUI ======================= */}
                                          <InspectionDataItem label="Drivable" value={toYesNo(latestInspection.drivable)} />
                                          <InspectionDataItem label="Windscreen" value={toYesNo(latestInspection.windscreen)} />
                                          <InspectionDataItem label="Intact Glass" value={toYesNo(latestInspection.intact_glass)} />
                                          <InspectionDataItem label="Paperwork" value={toYesNo(latestInspection.paperwork)} />
                                          <InspectionDataItem label="Keys" value={latestInspection.keys_qt} />
                                          <InspectionDataItem label="Spare Tire" value={toYesNo(latestInspection.spare_tire)} />
                                          {/* ===================================================================== */}
                                      </ul>
                                  </div>
                              </div>
                          </div>
                      ) : (
                          <p className="text-xs text-center text-gray-400 italic">No inspection has been performed yet.</p>
                      )}
                  </div>
                </>
            )}
            {/* Seção de Ações (Links da Galeria e Download) */}
            <div className="border-t border-gray-700 pt-3">
                <div className="flex justify-end gap-4 text-sm mb-3">
                    {hasPickupPhotos && <Link to={`/inspectiongallery/${vehicle.id}/Pickup`} state={{ vehicle, orderId }} className="text-blue-400 hover:underline font-semibold flex items-center gap-1"><TbPhoto/>Pickup Photos</Link>}
                    {hasDropoffPhotos && <Link to={`/inspectiongallery/${vehicle.id}/Dropoff`} state={{ vehicle, orderId }} className="text-blue-400 hover:underline font-semibold flex items-center gap-1"><TbPhoto/>Dropoff Photos</Link>}
                </div>
                {(hasPickupPhotos || hasDropoffPhotos) && <VehicleZipDownloader loadId={loadId} vehicleId={vehicle.id} />}
            </div>
        </div>
    );
}

// Componente para exibir a lista de veículos
function VehicleBentoBox({ vehicles, onVehicleClick, orderId, loadId, className }) {
  return (
    <BentoBox title="Vehicles" icon={TbTruck} className={className}>
      <div>
        {vehicles && vehicles.length > 0 ? (
          vehicles.map((vehicle) => (
            <VehicleCard 
                key={vehicle.id}
                vehicle={vehicle}
                orderId={orderId}
                loadId={loadId}
                onVehicleClick={onVehicleClick}
            />
          ))
        ) : (
          <p className="text-sm italic text-gray-400">No vehicles assigned</p>
        )}
      </div>
    </BentoBox>
  );
}

// ✨ FUNÇÃO AUXILIAR PARA FORMATAR A DATA ✨
function formatDate(dateString) {
  // Se a data for nula ou inválida, retorna 'N/A'
  if (!dateString) {
    return 'N/A';
  }

  // Cria um objeto Date. O 'Z' no final indica que a data é UTC.
  const date = new Date(dateString);

  // Usamos os métodos getUTC para evitar problemas com fuso horário
  const year = date.getUTCFullYear();
  // getUTCMonth() retorna de 0-11, então somamos 1
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');

  return `${month}/${day}/${year}`;
}

// Componente principal da página
export default function LoadPublicData() {
  const { id: loadId } = useParams();
  const [loadData, setLoadData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedVehicle, setSelectedVehicle] = useState(null);

  useEffect(() => {
    if (!loadId) {
      setError("Load ID is missing.");
      setLoading(false);
      return;
    }
    axios.get(`https://new.cajuu.app/api/loadtransport/public/${loadId}`)
      .then(response => { setLoadData(response.data.data); })
      .catch(err => {
        console.error("Failed to fetch load data:", err);
        setError("Could not retrieve load details.");
      })
      .finally(() => { setLoading(false); });
  }, [loadId]);

  if (loading) {
    return <div className="bg-[#1c1b19] text-white min-h-screen flex justify-center items-center"><TbLoader className="animate-spin text-4xl" /></div>;
  }
  if (error) {
    return <div className="bg-[#1c1b19] text-red-500 min-h-screen flex justify-center items-center"><p>{error}</p></div>;
  }

  return (
    <div className="bg-[#1c1b19] min-h-screen">
      <div className="text-white text-xl sm:text-2xl p-4 sm:p-6">
        <GroupedContainer><InfoSection title="LOAD PUBLIC DETAILS" /></GroupedContainer>
        <div className="mt-4 sm:mt-6">
          <div className="flex flex-col lg:flex-row w-full h-full gap-4 sm:gap-6">
            <div className="flex-1 p-2 sm:p-4 md:p-6 bg-[#1c1b19] text-white overflow-y-auto h-auto lg:h-[90vh] rounded-lg shadow-md">
              <h2 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6">Order ID: {loadData?.order_id}</h2>
              {loadData && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <BentoBox title="Pickup" icon={TbScanPosition}>
                    <p className="text-sm mb-2">{loadData.pickup_city}, {loadData.pickup_state}</p>
                    <p className="text-sm mb-2 text-gray-400">{formatDate(loadData.pickup_date || loadData.expected_pickup_date) || 'N/A'}</p>
                  </BentoBox>
                  <BentoBox title="Delivery" icon={TbTruckDelivery}>
                    <p className="text-sm mb-2">{loadData.delivery_city}, {loadData.delivery_state}</p>
                    <p className="text-sm mb-2 text-gray-400">{formatDate(loadData.dropoff_date || loadData.expected_dropoff_date) || 'N/A'}</p>
                  </BentoBox>
                  <BentoBox title="Status" icon={TbClipboardCheck}>
                    <p className="text-sm mb-2">{loadData.load_status.load_status_name}</p>
                  </BentoBox>
                  
                  <VehicleBentoBox 
                    vehicles={loadData.load_items} 
                    onVehicleClick={setSelectedVehicle} 
                    orderId={loadData.order_id} 
                    loadId={loadData.id}
                    className="col-span-1 md:col-span-2" 
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <VehicleImagePopup vehicle={selectedVehicle} isOpen={!!selectedVehicle} onClose={() => setSelectedVehicle(null)} />
    </div>
  );
}